import { useState } from 'react'
import { useServerFn } from '@tanstack/react-start'
import { useRouter } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'motion/react'
import {
  Copy,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lock,
  Tag,
  Smartphone,
  Share2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { createLinkFn } from '@/server/functions/links'

const SHORT_DOMAIN = 'l.zugl.in'

interface LShortenFormProps {
  onSuccess?: () => void
}

export function LShortenForm({ onSuccess }: LShortenFormProps = {}) {
  const [url, setUrl] = useState('')
  const [customCode, setCustomCode] = useState('')
  const [shortUrl, setShortUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Advanced options
  const [title, setTitle] = useState('')
  const [password, setPassword] = useState('')
  const [utmSource, setUtmSource] = useState('')
  const [utmMedium, setUtmMedium] = useState('')
  const [utmCampaign, setUtmCampaign] = useState('')
  const [androidRedirect, setAndroidRedirect] = useState('')
  const [iosRedirect, setIosRedirect] = useState('')

  const createLink = useServerFn(createLinkFn)
  const router = useRouter()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!url.trim()) {
      toast.error('Please enter a URL')
      return
    }

    setIsLoading(true)
    setShortUrl(null)

    try {
      const result = await createLink({
        data: {
          originalUrl: url.trim(),
          customCode: customCode.trim() || undefined,
          title: title.trim() || undefined,
          password: password.trim() || undefined,
          utmSource: utmSource.trim() || undefined,
          utmMedium: utmMedium.trim() || undefined,
          utmCampaign: utmCampaign.trim() || undefined,
          androidRedirect: androidRedirect.trim() || undefined,
          iosRedirect: iosRedirect.trim() || undefined,
        },
      })

      const newShortUrl = `https://${SHORT_DOMAIN}/${result.link.shortCode}`
      setShortUrl(newShortUrl)
      toast.success('Link shortened successfully!')
      setUrl('')
      setCustomCode('')
      setTitle('')
      setPassword('')
      setUtmSource('')
      setUtmMedium('')
      setUtmCampaign('')
      setAndroidRedirect('')
      setIosRedirect('')
      setShowAdvanced(false)

      // Invalidate router to refresh dashboard data instantly
      await router.invalidate()

      onSuccess?.()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to shorten link'
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!shortUrl) return

    try {
      await navigator.clipboard.writeText(shortUrl)
      setCopied(true)
      toast.success('Copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  return (
    <section className="relative py-24 bg-[#0a0a0a]" id="shorten">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 50px,
                        #9B87F5 50px,
                        #9B87F5 51px
                    ),
                    repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 50px,
                        #9B87F5 50px,
                        #9B87F5 51px
                    )`,
          }}
        />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[#1A1F2C]/80 backdrop-blur-xl rounded-lg border border-[#9B87F5]/20 overflow-hidden"
        >
          {/* Terminal header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0a0a0a] border-b border-[#9B87F5]/10">
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
            <div className="w-3 h-3 rounded-full bg-green-500/70" />
            <span className="text-[#8E9196] text-xs font-mono ml-2">
              shorten.exe
            </span>
          </div>

          <div className="p-6 md:p-8">
            {/* ASCII header */}
            <div className="text-center mb-6">
              <pre className="font-mono text-[#9B87F5] text-xs inline-block">
                {`┌─────────────────────────────────┐
│     SHORTEN YOUR LINK           │
│     ═══════════════════         │
└─────────────────────────────────┘`}
              </pre>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* URL Input */}
              <div className="relative">
                <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                  <span className="text-[#9B87F5]">$</span>
                  <span>enter_target_url:</span>
                </div>
                <Input
                  type="text"
                  placeholder="https://example.com/your-very-long-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full h-14 pl-5 pr-5 text-lg bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 rounded-lg font-mono focus:border-[#9B87F5] focus:ring-[#9B87F5]/20"
                />
              </div>

              {/* Custom code */}
              <div>
                <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                  <span className="text-[#9B87F5]">$</span>
                  <span>custom_alias (optional):</span>
                </div>
                <div className="flex">
                  <span className="px-4 py-3 bg-[#0a0a0a] border border-r-0 border-[#9B87F5]/20 rounded-l-lg text-[#9B87F5] font-mono text-sm">
                    {SHORT_DOMAIN}/
                  </span>
                  <Input
                    type="text"
                    placeholder="my-link"
                    value={customCode}
                    onChange={(e) =>
                      setCustomCode(
                        e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''),
                      )
                    }
                    maxLength={20}
                    className="flex-1 h-12 bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 rounded-l-none rounded-r-lg font-mono focus:border-[#9B87F5] focus:ring-[#9B87F5]/20"
                  />
                </div>
              </div>

              {/* Advanced options toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-[#9B87F5] hover:text-white font-mono text-sm transition-colors"
              >
                {showAdvanced ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {showAdvanced
                  ? 'hide_advanced_options'
                  : 'show_advanced_options'}
              </button>

              {/* Advanced options */}
              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-4 pt-4 border-t border-[#9B87F5]/10">
                      {/* Title */}
                      <div>
                        <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                          <Tag className="w-4 h-4 text-[#9B87F5]" />
                          <span>title:</span>
                        </div>
                        <Input
                          type="text"
                          placeholder="My awesome link"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          maxLength={100}
                          className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono"
                        />
                      </div>

                      {/* Password */}
                      <div>
                        <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                          <Lock className="w-4 h-4 text-[#9B87F5]" />
                          <span>password_protect:</span>
                        </div>
                        <Input
                          type="password"
                          placeholder="Leave empty for no password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          maxLength={100}
                          className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono"
                        />
                      </div>

                      {/* UTM Parameters */}
                      <div>
                        <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                          <Share2 className="w-4 h-4 text-[#9B87F5]" />
                          <span>utm_parameters:</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Input
                            type="text"
                            placeholder="utm_source"
                            value={utmSource}
                            onChange={(e) => setUtmSource(e.target.value)}
                            className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono text-sm"
                          />
                          <Input
                            type="text"
                            placeholder="utm_medium"
                            value={utmMedium}
                            onChange={(e) => setUtmMedium(e.target.value)}
                            className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono text-sm"
                          />
                          <Input
                            type="text"
                            placeholder="utm_campaign"
                            value={utmCampaign}
                            onChange={(e) => setUtmCampaign(e.target.value)}
                            className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono text-sm"
                          />
                        </div>
                      </div>

                      {/* Device-specific redirects */}
                      <div>
                        <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                          <Smartphone className="w-4 h-4 text-[#9B87F5]" />
                          <span>device_redirects:</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Input
                            type="text"
                            placeholder="Android URL (optional)"
                            value={androidRedirect}
                            onChange={(e) => setAndroidRedirect(e.target.value)}
                            className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono text-sm"
                          />
                          <Input
                            type="text"
                            placeholder="iOS URL (optional)"
                            value={iosRedirect}
                            onChange={(e) => setIosRedirect(e.target.value)}
                            className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white placeholder:text-[#8E9196]/50 font-mono text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit button */}
              <Button
                type="submit"
                disabled={isLoading || !url.trim()}
                className="w-full h-14 text-lg font-mono font-bold bg-[#9B87F5] hover:bg-[#8B5CF6] text-black rounded-lg shadow-lg shadow-[#9B87F5]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#9B87F5]/30 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    PROCESSING...
                  </>
                ) : (
                  '[ EXECUTE SHORTEN ]'
                )}
              </Button>
            </form>

            {/* Result */}
            <AnimatePresence>
              {shortUrl && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mt-6 p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/30"
                >
                  <div className="font-mono text-sm text-[#8E9196] mb-2">
                    <span className="text-green-400">✓</span> Link created
                    successfully:
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xl font-mono font-bold text-[#9B87F5] truncate">
                        {shortUrl}
                      </p>
                    </div>
                    <Button
                      onClick={handleCopy}
                      className={`flex-shrink-0 font-mono transition-all duration-300 ${
                        copied
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-[#9B87F5] hover:bg-[#8B5CF6]'
                      } text-black`}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          COPIED
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          COPY
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
